import { getPaddleBillingConfig } from "../../utils/paddleBilling";

const DEFAULT_CATALOG_ERROR =
  "Billing plans are unavailable or incomplete. Please try again.";
const CONFIGURATION_ERROR =
  "Billing plan configuration is invalid. Please contact support.";
const INVALID_RESPONSE_ERROR =
  "Billing plan catalog is invalid. Please try again.";

function redactPriceId(priceId) {
  const value = String(priceId || "");
  return value.length > 10 ? `${value.slice(0, 6)}…${value.slice(-4)}` : "[redacted]";
}

function reportCatalogIssue(code, details = {}) {
  if (process.env.NODE_ENV !== "production") {
    // Price IDs are identifiers, but keep diagnostics safe and never log env values.
    // eslint-disable-next-line no-console
    console.error("[BillingCatalog]", {
      code,
      ...details,
      ids: details.ids?.map(redactPriceId),
    });
  }
}

function failCatalog(code, message, details = {}) {
  reportCatalogIssue(code, details);
  const error = new Error(message);
  error.catalogCode = code;
  error.userMessage = message;
  return error;
}

function getConfiguredPriceIds(config) {
  if (config?.priceMapError) {
    throw failCatalog("invalid-price-map", CONFIGURATION_ERROR, {
      reason: config.priceMapError,
    });
  }

  const configuredValues = Object.values(config?.priceMap || {});

  if (!configuredValues.length) {
    throw failCatalog("empty-price-map", CONFIGURATION_ERROR);
  }

  if (
    configuredValues.some(
      (priceId) => typeof priceId !== "string" || !priceId.trim()
    )
  ) {
    throw failCatalog("invalid-price-id", CONFIGURATION_ERROR);
  }

  const configuredPriceIds = configuredValues.map((priceId) => priceId.trim());
  const duplicateIds = configuredPriceIds.filter(
    (priceId, index) => configuredPriceIds.indexOf(priceId) !== index
  );

  if (duplicateIds.length) {
    throw failCatalog("duplicate-configured-price-id", CONFIGURATION_ERROR, {
      ids: [...new Set(duplicateIds)],
    });
  }

  return configuredPriceIds;
}

export function validateBillingPlans(apiPlans, config = getPaddleBillingConfig()) {
  const configuredPriceIds = getConfiguredPriceIds(config);
  const configuredPriceIdSet = new Set(configuredPriceIds);

  if (!Array.isArray(apiPlans) || apiPlans.length === 0) {
    throw failCatalog("empty-api-catalog", DEFAULT_CATALOG_ERROR);
  }

  const apiPriceIds = [];
  for (const plan of apiPlans) {
    if (
      !plan ||
      typeof plan !== "object" ||
      typeof plan.price_id !== "string" ||
      !plan.price_id.trim()
    ) {
      throw failCatalog("invalid-api-plan", INVALID_RESPONSE_ERROR);
    }

    const priceId = plan.price_id.trim();
    if (apiPriceIds.includes(priceId)) {
      throw failCatalog("duplicate-api-price-id", INVALID_RESPONSE_ERROR, {
        ids: [priceId],
      });
    }

    apiPriceIds.push(priceId);
  }

  const missingPriceIds = configuredPriceIds.filter(
    (priceId) => !apiPriceIds.includes(priceId)
  );

  if (missingPriceIds.length) {
    throw failCatalog("missing-configured-price-id", DEFAULT_CATALOG_ERROR, {
      ids: missingPriceIds,
    });
  }

  const approvedPlans = apiPlans.filter((plan) =>
    configuredPriceIdSet.has(plan.price_id.trim())
  );

  if (
    approvedPlans.some(
      (plan) =>
        typeof plan.plan_name !== "string" ||
        !plan.plan_name.trim() ||
        !Number.isInteger(plan.allowed_groups) ||
        plan.allowed_groups < 0
    )
  ) {
    throw failCatalog("invalid-api-plan-metadata", INVALID_RESPONSE_ERROR);
  }

  return approvedPlans.map((plan) => ({
    ...plan,
    price_id: plan.price_id.trim(),
  }));
}

export function getBillingCatalogErrorMessage(error) {
  if (error?.catalogCode) {
    return error.userMessage || DEFAULT_CATALOG_ERROR;
  }

  return "Unable to load Billing plans. Please try again.";
}

export function getBillingPeriodFromPlanName(planName) {
  const normalizedName = String(planName || "").trim().toLowerCase();

  if (normalizedName.startsWith("monthly")) {
    return "monthly";
  }

  if (normalizedName.startsWith("yearly")) {
    return "yearly";
  }

  return "";
}

export function getBillingPlanTypeFromName(planName) {
  const period = getBillingPeriodFromPlanName(planName);
  return period ? ` / ${period === "monthly" ? "month" : "year"}` : "";
}

export function getBillingTierLabel(planName) {
  const value = String(planName || "").trim();
  const period = getBillingPeriodFromPlanName(value);
  return period ? value.slice(period.length).trim() || value : value;
}

export function getIntegerPriceLabel(priceLabel) {
  if (priceLabel == null || priceLabel === "") {
    return "";
  }

  return String(priceLabel).replace(/(\d+)[.,]\d+(?=\D*$)/, "$1");
}

export function getBillingPricePreviewLabel(lineItem) {
  return getIntegerPriceLabel(
    lineItem?.formatted_unit_totals?.subtotal ||
      lineItem?.formattedUnitTotals?.subtotal ||
      lineItem?.formatted_totals?.subtotal ||
      lineItem?.formattedTotals?.subtotal ||
      ""
  );
}
